import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getDraws } from "@/lib/api";
import { TIPO_LABEL, isVisibleTipo, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { GameResult } from "@/components/GameResult";

const DESC = {
  triple: "Triple A y Triple B (000–999) con Estado, generados con aleatoriedad verificable.",
  lotto: "Combinaciones de números únicos, generadas con aleatoriedad verificable.",
  animalitos: "La figura ganadora entre los animalitos, incluyendo el comodín, con trazabilidad total.",
  rifas: "Boletos ganadores seleccionados de forma provably-fair.",
  raspaditos: "Premios instantáneos por tabla de probabilidad auditable.",
};

export default function GameResults() {
  const { tipo } = useParams();
  const [draws, setDraws] = useState(null);

  useEffect(() => {
    if (!isVisibleTipo(tipo)) { setDraws([]); return; }
    setDraws(null);
    getDraws({ tipo, limit: 60 }).then(setDraws).catch(() => setDraws([]));
  }, [tipo]);

  if (!isVisibleTipo(tipo)) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-20 text-center" data-testid="game-not-available">
        <h1 className="font-display text-2xl font-bold">Juego no disponible</h1>
        <p className="mt-2 text-muted-foreground">Este juego no está disponible públicamente por ahora.</p>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div data-theme={tipo}>
      <section className="relative overflow-hidden border-b border-border" style={{ background: "hsl(var(--game-surface))" }}>
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(var(--game-accent))" }}>Resultados</span>
          <h1 className="mt-1 font-display text-4xl font-bold" style={{ color: "hsl(var(--game-accent))" }}>{TIPO_LABEL[tipo] || tipo}</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">{DESC[tipo]}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-10">
        {draws === null ? (
          <p className="text-muted-foreground">Cargando…</p>
        ) : draws.length === 0 ? (
          <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">Aún no hay sorteos de este juego. Créalos desde la <Link to="/consola/sorteos" className="text-primary underline">consola</Link>.</CardContent></Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {draws.map((d, i) => (
              <motion.div key={d.id} initial={{ y: 12 }} animate={{ y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/sorteo/${d.id}`} data-testid={`game-draw-${d.id}`}>
                  <Card className="h-full border-border transition-shadow hover:shadow-md">
                    <div className="h-1.5" style={{ background: "hsl(var(--game-accent))" }} />
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{d.nombre}</span>
                        <StatusBadge estado={d.estado} />
                      </div>
                      <div className="mt-4 min-h-[64px]">
                        <GameResult tipo={d.tipo} resultado={d.resultado} />
                      </div>
                      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{formatDate(d.executed_at || d.created_at)}</span>
                        <span className="inline-flex items-center gap-1 font-medium text-foreground">Detalle <ArrowRight className="h-3.5 w-3.5" /></span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
